import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  Tag,
  Space,
  Alert,
  Descriptions,
  Statistic,
  Row,
  Col,
  Spin,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  WarningOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  getExcessStock,
  applyExcessStock,
  findMatchingExcess,
} from "../../api/wastageApi";
import { getAllJobs } from "../../api/jobApi";

const ExcessStockManager = () => {
  const [loading, setLoading] = useState(false);
  const [excessData, setExcessData] = useState({ excess: [], total: 0 });
  const [filters, setFilters] = useState({
    page: 1,
    count: 20,
    status: undefined,
    ply: undefined,
  });

  const [utilizeModalVisible, setUtilizeModalVisible] = useState(false);
  const [findMatchModalVisible, setFindMatchModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [matchingJobs, setMatchingJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [utilizeForm] = Form.useForm();
  const [matchForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getExcessStock(filters);
      setExcessData({
        excess: data.excess || [],
        total: data.total || 0,
      });
    } catch (error) {
      toast.error("Failed to load excess stock");
    }
    setLoading(false);
  };

  const handleOpenUtilize = async (item) => {
    setSelectedItem(item);
    setUtilizeModalVisible(true);
    utilizeForm.resetFields();
    utilizeForm.setFieldsValue({ quantity: item.available_qty });
    setJobsLoading(true);
    try {
      const data = await getAllJobs();
      setMatchingJobs(data.jobs || []);
    } catch {
      setMatchingJobs([]);
    }
    setJobsLoading(false);
  };

  const handleUtilize = async (values) => {
    try {
      await applyExcessStock(selectedItem.id, {
        job_id: values.job_id,
        quantity: values.quantity,
      });
      toast.success("Excess stock utilized successfully");
      setUtilizeModalVisible(false);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to utilize excess stock"
      );
    }
  };

  const handleFindMatch = async (values) => {
    setMatchLoading(true);
    try {
      const result = await findMatchingExcess(values);
      setMatchResults(result);
    } catch (error) {
      toast.error("Failed to find matches");
    }
    setMatchLoading(false);
  };

  const excessList = excessData.excess;

  // Calculate summary stats
  const totalItems = excessData.total;
  const availableItems = excessList.filter(
    (item) => item.status === "available" || item.available_qty > 0
  );
  const agingItems = excessList.filter(
    (item) =>
      item.created_at &&
      dayjs().diff(dayjs(item.created_at), "day") > 30
  );

  const columns = [
    {
      title: "Source Job",
      dataIndex: ["source_job", "job_number"],
      key: "job",
      render: (text) => text || "-",
    },
    {
      title: "Stage",
      dataIndex: ["stage", "name"],
      key: "stage",
      render: (text) => text || "-",
    },
    {
      title: "Specs",
      key: "specs",
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          {record.roll_size != null && (
            <div>Roll Size: {record.roll_size}&quot;</div>
          )}
          {record.cutting_size != null && (
            <div>Cut Size: {record.cutting_size}&quot;</div>
          )}
          {record.ply && <div>Ply: {record.ply}</div>}
          {record.flute_type && <div>Flute: {record.flute_type}</div>}
        </div>
      ),
    },
    {
      title: "Qty",
      key: "quantity",
      render: (_, record) => (
        <div>
          <div>Original: {record.original_qty?.toLocaleString()}</div>
          <div style={{ color: "#52c41a", fontWeight: 600 }}>
            Available: {record.available_qty?.toLocaleString()}
          </div>
        </div>
      ),
    },
    {
      title: "Age",
      dataIndex: "created_at",
      key: "age",
      render: (date) => {
        if (!date) return "-";
        const days = dayjs().diff(dayjs(date), "day");
        return (
          <Tag
            color={days > 30 ? "red" : days > 14 ? "orange" : "green"}
          >
            {days} days
          </Tag>
        );
      },
    },
    {
      title: "Cost/Unit",
      dataIndex: "cost_per_unit",
      key: "cost",
      render: (v) => (v ? `₹${v}` : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const colorMap = {
          available: "green",
          used: "cyan",
          expired: "red",
          reserved: "blue",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Customer-Specific",
      dataIndex: "is_customer_specific",
      key: "customer_specific",
      render: (v) =>
        v ? <Tag color="purple">Yes</Tag> : <Tag>No</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) =>
        (record.status === "available" || record.available_qty > 0) && (
          <Button
            size="small"
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => handleOpenUtilize(record)}
          >
            Utilize
          </Button>
        ),
    },
  ];

  return (
    <div>
      {/* Summary */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Total Excess Items" value={totalItems} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Available"
              value={availableItems.length}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Aging (>30 days)"
              value={agingItems.length}
              valueStyle={{
                color: agingItems.length > 0 ? "#cf1322" : "#3f8600",
              }}
              prefix={agingItems.length > 0 ? <WarningOutlined /> : null}
            />
          </Card>
        </Col>
      </Row>

      {agingItems.length > 0 && (
        <Alert
          message="Aging Excess Stock"
          description={`${agingItems.length} item(s) have been in excess inventory for over 30 days. Consider utilizing them for upcoming jobs.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title="Excess Stock Inventory"
        extra={
          <Space>
            <Button
              icon={<SearchOutlined />}
              onClick={() => setFindMatchModalVisible(true)}
            >
              Find Match
            </Button>
            <Select
              style={{ width: 140 }}
              placeholder="Status"
              allowClear
              value={filters.status}
              onChange={(v) =>
                setFilters({ ...filters, status: v, page: 1 })
              }
            >
              <Select.Option value="available">Available</Select.Option>
              <Select.Option value="used">Used</Select.Option>
              <Select.Option value="expired">Expired</Select.Option>
            </Select>
            <Select
              style={{ width: 120 }}
              placeholder="Ply"
              allowClear
              value={filters.ply}
              onChange={(v) =>
                setFilters({ ...filters, ply: v, page: 1 })
              }
            >
              <Select.Option value={3}>3-Ply</Select.Option>
              <Select.Option value={5}>5-Ply</Select.Option>
              <Select.Option value={7}>7-Ply</Select.Option>
            </Select>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={excessList}
          rowKey="id"
          loading={loading}
          pagination={{
            current: filters.page,
            pageSize: filters.count,
            total: excessData.total,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
            onChange: (page, count) =>
              setFilters({ ...filters, page, count }),
          }}
        />
      </Card>

      {/* Utilize Modal */}
      <Modal
        title="Utilize Excess Stock"
        open={utilizeModalVisible}
        onCancel={() => setUtilizeModalVisible(false)}
        onOk={() => utilizeForm.submit()}
        destroyOnClose
      >
        {selectedItem && (
          <Descriptions
            size="small"
            column={2}
            bordered
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Source Job">
              {selectedItem.source_job?.job_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Available Qty">
              {selectedItem.available_qty?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Roll Size">
              {selectedItem.roll_size || "-"}&quot;
            </Descriptions.Item>
            <Descriptions.Item label="Ply">
              {selectedItem.ply || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}

        <Form form={utilizeForm} layout="vertical" onFinish={handleUtilize}>
          <Form.Item
            name="job_id"
            label="Assign to Job"
            rules={[{ required: true, message: "Select a job" }]}
          >
            <Select
              placeholder="Select matching job"
              showSearch
              optionFilterProp="children"
              loading={jobsLoading}
              notFoundContent={
                jobsLoading ? <Spin size="small" /> : "No jobs found"
              }
            >
              {matchingJobs.map((j) => (
                <Select.Option key={j.id} value={j.id}>
                  {j.job_number} - {j.customer?.name || ""}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="quantity"
            label="Quantity to Use"
            rules={[{ required: true, message: "Enter quantity" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={selectedItem?.available_qty}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Find Match Modal */}
      <Modal
        title="Find Matching Excess Stock"
        open={findMatchModalVisible}
        onCancel={() => {
          setFindMatchModalVisible(false);
          setMatchResults(null);
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form form={matchForm} layout="vertical" onFinish={handleFindMatch}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="roll_size" label="Roll Size">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  addonAfter="&quot;"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cutting_size" label="Cutting Size">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  addonAfter="&quot;"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="ply" label="Ply">
                <Select placeholder="Ply" allowClear>
                  <Select.Option value={3}>3</Select.Option>
                  <Select.Option value={5}>5</Select.Option>
                  <Select.Option value={7}>7</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="flute_type" label="Flute Type">
                <Select placeholder="Flute" allowClear>
                  <Select.Option value="A">A</Select.Option>
                  <Select.Option value="B">B</Select.Option>
                  <Select.Option value="C">C</Select.Option>
                  <Select.Option value="E">E</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quantity_needed" label="Qty Needed">
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={matchLoading}
                  block
                >
                  Search
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {matchResults && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={8}>
                <Statistic
                  title="Candidates"
                  value={matchResults.candidates?.length || 0}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Available"
                  value={matchResults.total_available || 0}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Can Fulfill"
                  value={matchResults.can_fulfill ? "Yes" : "No"}
                  valueStyle={{
                    color: matchResults.can_fulfill
                      ? "#52c41a"
                      : "#ff4d4f",
                  }}
                />
              </Col>
            </Row>
            {matchResults.candidates?.length > 0 && (
              <Table
                size="small"
                dataSource={matchResults.candidates}
                rowKey="id"
                pagination={false}
                columns={[
                  {
                    title: "Source",
                    dataIndex: ["source_job", "job_number"],
                    render: (v) => v || "-",
                  },
                  {
                    title: "Available",
                    dataIndex: "available_qty",
                  },
                  {
                    title: "Roll Size",
                    dataIndex: "roll_size",
                    render: (v) => (v ? `${v}"` : "-"),
                  },
                  {
                    title: "Action",
                    key: "action",
                    render: (_, record) => (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => {
                          setFindMatchModalVisible(false);
                          setMatchResults(null);
                          handleOpenUtilize(record);
                        }}
                      >
                        Use
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        )}
      </Modal>
    </div>
  );
};

export default ExcessStockManager;
